import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useIntl, useLocation, useParams } from 'umi';
import Loading from '@/pages/dashboard/components/Loadings';
import { IframeUrl } from '@home/components/IframeUrl';
import StudioState from '@home/pages/studio/components/StudioState';
import { StudioContext } from '@home/pages/studio/context';
import type { IDashboardItem, IGameInfo } from '@home/pages/studio/type';
import { isDatabrainPage, matchDatabrainPageRouteKey } from '@home/pages/studio/utils';
import {
  appendEmbeddedPageOptions,
  type IEmbeddedPageOptions,
} from '@home/hooks/useEmbeddedPageOptions';

interface IRemoveModuleConfig {
  name?: string;
  selectors: string[];
}

interface IStudioGameQueryParamConfig {
  paramName: string;
  gameField?: string;
  value?: string;
}

/** 注入到 iframe 内的样式配置，全部由 pageConfig 决定，组件不内置任何选择器 */
interface IIframeStyleConfig {
  /** 隐藏的模块，生成 display: none */
  removeModules?: IRemoveModuleConfig[];
  /** 内容区容器，清除被隐藏菜单留下的偏移与宽度限制 */
  resetContentSelectors?: string[];
  /** 布局容器，将 flex 布局改回常规文档流，避免隐藏子节点后剩余节点被拉伸 */
  resetLayoutFlowSelectors?: string[];
  /** 加载态容器，撑满视口高度，避免子页面 loading 期间高度塌陷 */
  fullHeightSelectors?: string[];
  showSelectors?: string[];
}

interface IDatabrainLinkIframeProps extends IIframeStyleConfig {
  data?: {
    studioData?: {
      studio_dashboard_list?: IDashboardItem[];
      game_list?: IGameInfo[];
    };
  };
  inheritQueryParams?: boolean;
  iframeHeight?: React.CSSProperties['height'];
  /** 内置兜底高度；优先级低于页面配置 iframeHeight 和管理端 iframe_height。 */
  defaultIframeHeight?: React.CSSProperties['height'];
  /** 是否根据同源 iframe 内容自适应高度，默认 false，由 pageConfig 开启 */
  autoSize?: boolean;
  studioGameQueryParams?: IStudioGameQueryParamConfig[];
  embedOptions?: IEmbeddedPageOptions;
}

const HIDE_STYLE_ID = 'dbh-databrain-link-hide-modules';

// 字面量默认值每次渲染都是新引用，会让依赖它们的 useMemo/useCallback 失效
const EMPTY_MODULES: IRemoveModuleConfig[] = [];
const EMPTY_SELECTORS: string[] = [];
const EMPTY_QUERY_PARAMS: IStudioGameQueryParamConfig[] = [];

const normalizePath = (path?: string) =>
  String(path ?? '').trim().replace(/\/+$/, '').toLowerCase();

const decodeRouteParam = (value?: string) => {
  try {
    return decodeURIComponent(value || '');
  } catch (e) {
    return value || '';
  }
};

const getGameFieldValue = (game: Record<string, any> | null | undefined, field: string) => {
  const value = game?.[field];
  return value === undefined || value === null ? '' : String(value).trim();
};

const buildDatabrainUrl = (url?: string) => {
  const rawUrl = String(url ?? '').trim();
  if (!rawUrl) {
    return '';
  }

  try {
    const parsedUrl = new URL(rawUrl, window.location.origin);
    if (parsedUrl.origin !== window.location.origin) {
      return '';
    }
    return `${window.location.origin}${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (e) {
    return '';
  }
};

const appendStudioGameQueryParams = (
  url: string,
  game: Record<string, any> | null | undefined,
  queryParams: IStudioGameQueryParamConfig[] = [],
) => {
  if (!url || !queryParams.length) {
    return url;
  }

  const parsedUrl = new URL(url, window.location.origin);
  queryParams.forEach(({ paramName, gameField, value }) => {
    const queryValue = value === undefined ? getGameFieldValue(game, gameField || '') : String(value).trim();
    if (paramName && queryValue) {
      parsedUrl.searchParams.set(paramName, queryValue);
    }
  });
  return parsedUrl.toString();
};

const getRemoveSelectors = (removeModules: IRemoveModuleConfig[] = []) => (
  removeModules.flatMap((moduleConfig) => moduleConfig.selectors || [])
);

const injectIframeStyle = (iframeDocument: Document, config: IIframeStyleConfig) => {
  const {
    removeModules = EMPTY_MODULES,
    resetContentSelectors = EMPTY_SELECTORS,
    resetLayoutFlowSelectors = EMPTY_SELECTORS,
    fullHeightSelectors = EMPTY_SELECTORS,
    showSelectors = EMPTY_SELECTORS,
  } = config;
  const removeSelectors = getRemoveSelectors(removeModules);
  const styleContent = [
    removeSelectors.length ? `${removeSelectors.join(',')} { display: none !important; }` : '',
    resetContentSelectors.length ? `${resetContentSelectors.join(',')} { margin-left: 0 !important; padding-left: 0 !important; left: 0 !important; width: 100% !important; max-width: none !important; }` : '',
    resetLayoutFlowSelectors.length ? `${resetLayoutFlowSelectors.join(',')} { display: block !important; }` : '',
    fullHeightSelectors.length ? `${fullHeightSelectors.join(',')} { min-height: 100vh !important; box-sizing: border-box !important; }` : '',
    showSelectors.length ? `${showSelectors.map((selector) => `:root ${selector}`).join(',')} { display: block !important; }` : '',
  ].filter(Boolean).join('\n');

  if (!styleContent) {
    return;
  }

  let styleNode = iframeDocument.getElementById(HIDE_STYLE_ID) as HTMLStyleElement | null;
  if (!styleNode) {
    styleNode = iframeDocument.createElement('style');
    styleNode.id = HIDE_STYLE_ID;
    iframeDocument.head?.appendChild(styleNode);
  }
  styleNode.textContent = styleContent;
};

// 注入样式表即可覆盖 iframe 内后续动态渲染的节点，无需轮询逐个设置 inline style
const applyIframeHideStyle = (
  iframeDocument: Document | null | undefined,
  config: IIframeStyleConfig,
) => {
  if (!iframeDocument) {
    return;
  }

  try {
    injectIframeStyle(iframeDocument, config);
  } catch (e) {
    // Cross-origin or not-ready iframe documents cannot be modified.
  }
};

const DatabrainLinkIframe = (props: IDatabrainLinkIframeProps) => {
  const {
    data,
    inheritQueryParams = true,
    removeModules = EMPTY_MODULES,
    resetContentSelectors = EMPTY_SELECTORS,
    resetLayoutFlowSelectors = EMPTY_SELECTORS,
    fullHeightSelectors = EMPTY_SELECTORS,
    showSelectors = EMPTY_SELECTORS,
    iframeHeight,
    defaultIframeHeight,
    autoSize = false,
    studioGameQueryParams = EMPTY_QUERY_PARAMS,
    embedOptions,
  } = props;
  const location = useLocation();
  const { locale } = useIntl();
  const {
    studioId,
    gameCode,
    linkKey,
    linkId,
    tabId,
  } = useParams() as {
    studioId?: string;
    gameCode?: string;
    linkKey?: string;
    linkId?: string;
    tabId?: string;
  };
  const { currentGame, currentMenu, menuPermission } = useContext(StudioContext);
  const routeLinkKey = linkKey || linkId || tabId;

  const studioDashboardMenu = useMemo(() => {
    const studioPathPrefix = `/studio/${studioId || ''}`;
    const currentStudioPath = location.pathname.startsWith(studioPathPrefix)
      ? location.pathname.slice(studioPathPrefix.length)
      : location.pathname;
    return data?.studioData?.studio_dashboard_list?.find((item) => (
      isDatabrainPage(item) &&
      (
        routeLinkKey
          ? matchDatabrainPageRouteKey(item, decodeRouteParam(routeLinkKey))
          : normalizePath(item?.path) === normalizePath(currentStudioPath)
      )
    ));
  }, [data?.studioData?.studio_dashboard_list, location.pathname, routeLinkKey, studioId]);

  const menu = useMemo(() => {
    if (isDatabrainPage(currentMenu)) {
      return currentMenu;
    }
    return studioDashboardMenu;
  }, [currentMenu, studioDashboardMenu]);

  const routeGame = useMemo(() => data?.studioData?.game_list?.find((game) => (
    game?.game_code === gameCode || game?.unified_edition_id === gameCode
  )), [data?.studioData?.game_list, gameCode]);

  const studioGame = useMemo(() => {
    if (currentGame?.game_code || currentGame?.unified_edition_id) {
      return currentGame;
    }
    return routeGame;
  }, [currentGame, routeGame]);

  const iframeUrl = useMemo(() => {
    const databrainUrl = buildDatabrainUrl(menu?.url);
    const urlWithGame = appendStudioGameQueryParams(
      databrainUrl,
      studioGame,
      studioGameQueryParams,
    );
    return appendEmbeddedPageOptions(urlWithGame, embedOptions);
  }, [embedOptions, menu?.url, studioGame, studioGameQueryParams]);
  const effectiveIframeHeight = iframeHeight ?? menu?.iframe_height ?? defaultIframeHeight;
  const iframeStyle = useMemo(() => {
    if (effectiveIframeHeight === undefined || effectiveIframeHeight === null || effectiveIframeHeight === '') {
      return undefined;
    }
    const heightValue = typeof effectiveIframeHeight === 'number'
      ? `${effectiveIframeHeight}px`
      : effectiveIframeHeight;
    // 自适应开启时只保留最小高度，避免写死 height 把内容撑开后的高度裁掉
    if (autoSize) {
      return { minHeight: heightValue };
    }
    return { height: heightValue, minHeight: heightValue };
  }, [autoSize, effectiveIframeHeight]);
  const [iframeLoading, setIframeLoading] = useState(false);

  useEffect(() => {
    setIframeLoading(Boolean(iframeUrl));
  }, [iframeUrl]);

  const styleConfig = useMemo<IIframeStyleConfig>(() => ({
    removeModules,
    resetContentSelectors,
    resetLayoutFlowSelectors,
    fullHeightSelectors,
    showSelectors,
  }), [fullHeightSelectors, removeModules, resetContentSelectors, resetLayoutFlowSelectors, showSelectors]);

  // 子页面 mount 早于 load 事件，此路径能更早隐藏模块，减少内容闪现
  const handleIframeMessage = useCallback((event: MessageEvent) => {
    if (event?.data?.type !== 'iframeMounted') {
      return;
    }
    try {
      applyIframeHideStyle((event.source as Window | null)?.document, styleConfig);
    } catch (e) {
      // Cross-origin messages are ignored.
    }
  }, [styleConfig]);

  const handleIframeLoad = useCallback((event: React.SyntheticEvent<HTMLIFrameElement>) => {
    applyIframeHideStyle(event.currentTarget.contentDocument, styleConfig);
    setIframeLoading(false);
  }, [styleConfig]);

  if (menuPermission === false) {
    return <StudioState isNoPermission />;
  }

  if (!iframeUrl) {
    return <StudioState />;
  }

  return (
    <div
      className={classNames(
        'dbh-studio-databrain-link-iframe-box',
        autoSize && 'dbh-studio-databrain-link-iframe-box--auto-size',
      )}
      style={iframeStyle}
    >
      {iframeLoading ? <Loading className="dbh-studio-databrain-link-loading" /> : null}
      <IframeUrl
        url={iframeUrl}
        key={iframeUrl + (locale || '')}
        inheritQueryParams={inheritQueryParams}
        autoSize={autoSize}
        className={`db-studio-iframe-wrap ${iframeLoading ? 'is-loading' : ''}`}
        style={iframeStyle}
        iframeStyle={iframeStyle}
        onMessage={handleIframeMessage}
        onLoad={handleIframeLoad}
      />
    </div>
  );
};

export default DatabrainLinkIframe;
